/**
 * CreateVendorModal - Modal for creating new vendors
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { vendorsAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const VENDOR_TYPES = [
  { value: 'awards', label: 'Awards' },
  { value: 'printing', label: 'Printing' },
  { value: 'large_format', label: 'Large Format' },
  { value: 'promo', label: 'Promo' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'services', label: 'Services' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_TERMS = [
  { value: 'NET_30', label: 'Net 30' },
  { value: 'NET_15', label: 'Net 15' },
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'PREPAID', label: 'Prepaid' },
];

const CreateVendorModal = ({ open, onOpenChange, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    vendor_type: 'other',
    email: '',
    phone: '',
    payment_terms: 'NET_30',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setFormData({
        name: '',
        vendor_type: 'other',
        email: '',
        phone: '',
        payment_terms: 'NET_30',
        notes: '',
      });
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    setSubmitting(true);
    try {
      const result = await vendorsAPI.createVendor({
        name: formData.name,
        vendor_type: formData.vendor_type,
        email: formData.email || null,
        phone: formData.phone || null,
        payment_terms: formData.payment_terms || null,
        notes: formData.notes || null,
        is_active: true,
      });
      toast.success('Vendor created successfully');
      onOpenChange(false);
      onSuccess?.(result.id);
    } catch (err) {
      toast.error(err.message || 'Failed to create vendor');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-vendor-modal">
        <DialogHeader>
          <DialogTitle>Create New Vendor</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Vendor Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Vendor name"
              data-testid="vendor-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Vendor Type</Label>
            <select
              value={formData.vendor_type}
              onChange={(e) => handleChange('vendor_type', e.target.value)}
              className="w-full h-10 px-3 border rounded-md bg-white"
            >
              {VENDOR_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="vendor@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Terms</Label>
            <select
              value={formData.payment_terms}
              onChange={(e) => handleChange('payment_terms', e.target.value)}
              className="w-full h-10 px-3 border rounded-md bg-white"
            >
              {PAYMENT_TERMS.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !formData.name.trim()}>
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              ) : (
                'Create Vendor'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateVendorModal;
